/**
 * @import { BBox } from 'geojson'
 * @import { Place } from '@/features/world-map/types/place'
 */

import { useMapViewportFilter } from '@/features/world-map/hooks/use-map-viewport-filter';
import { useDebounce } from '@/hooks/use-debounce';
import { useMemo } from 'react';
import { useCruiseSearch } from '../api/search-cruises';
import { useFiltersContext } from '../context/filters';
import { usePlaceInfoContext } from '../context/place-info';

export function useCruises() {
  const bbox = useMapViewportFilter();
  const { filters } = useFiltersContext();
  const { placeInfo } = usePlaceInfoContext();
  const searchParams = useMemo(() => ({ bbox, filters }), [bbox, filters]);
  const debouncedSearchParams = useDebounce(searchParams);
  const cruises = useCruiseSearch(debouncedSearchParams.filters);

  /**
   * @param {BBox} bbox
   * @param {Place | undefined} place
   * @returns {google.maps.LatLngBounds}
   */
  const getFilterBounds = (bbox, place) => {
    const filterBbox = place
      ? [
          ...place.position.map((coordinate) => coordinate - 0.000001),
          ...place.position.map((coordinate) => coordinate + 0.000001),
        ]
      : bbox;

    const sw = { lng: filterBbox[0], lat: filterBbox[1] };
    const ne = { lng: filterBbox[2], lat: filterBbox[3] };
    const bounds = new google.maps.LatLngBounds(sw, ne);
    return bounds.isEmpty() ? new google.maps.LatLngBounds({ lat: -90, lng: -180 }, { lat: 90, lng: 180 }) : bounds;
  };

  /**
   * @param {ReturnType<useCruiseSearch>['data']} data
   * @returns {ReturnType<useCruiseSearch>['data']}
   */
  const filterData = (data) => {
    if (!globalThis?.google?.maps?.LatLngBounds || !data?.length) {
      return data;
    }

    const bounds = getFilterBounds(bbox, placeInfo?.place);
    const cruises = data.filter((cruise) => {
      return cruise.positions.some(([lng, lat]) => bounds.contains({ lat, lng }));
    });

    return cruises.length === data.length ? data : cruises;
  };

  const filteredData = filterData(cruises.data);
  return { ...cruises, data: filteredData, totalRecords: filteredData?.length ?? 0 };
}
